import axios from "axios";
import apiEndpoints from "../config/apiEndpoints";
import getApiEndpointUrl from "../helpers/getApiEndpointUrl";
import BaseUrl from "../types/BaseUrl";
import IArticleCampaign from "../types/IArticleCampaign";
import IArticleCampaignV2 from "../types/IArticleCampaignV2";
import ICategoryCampaign from "../types/ICategoryCampaign";
import ICategoryCampaignV2 from "../types/ICategoryCampaignV2";
import IMerchandisingSlot from "../types/IMerchandisingSlot";
import IMerchandisingSlots from "../types/IMerchandisingSlots";
import ISelectedArticles from "../types/ISelectedArticles";
import ISelectedExtras from "../types/ISelectedExtras";
import Options from "../types/Options";

export default class CampaignApi {
  private baseUrl: BaseUrl;
  private options: Options;

  constructor(baseUrl: BaseUrl, options: Options) {
    this.baseUrl = baseUrl;
    this.options = options;
  }

  public async getAllMerchandisingSlotsInfo(campaignId: string): Promise<IMerchandisingSlots> {
    return await this.call<IMerchandisingSlots>(campaignId, "slots");
  }

  public async getMerchandisingSlotInfo(campaignId: string, slot_id: string): Promise<IMerchandisingSlot> {
    return await this.call<IMerchandisingSlot>(campaignId, "slot", { slot_id: slot_id });
  }

  public async getAllCategoriesCampaignInfo(campaignId: string): Promise<ICategoryCampaign[] | ICategoryCampaignV2[]> {
    return await (this.options.getApiVersion() === 1
      ? this.call<ICategoryCampaign[]>(campaignId, "categories")
      : this.call<ICategoryCampaignV2[]>(campaignId, "categories"));
  }

  public async getCategoryCampaignInfo(
    campaignId: string,
    category: string
  ): Promise<ICategoryCampaign | ICategoryCampaignV2> {
    return await (this.options.getApiVersion() === 1
      ? this.call<ICategoryCampaign>(campaignId, "category", { category: category })
      : this.call<ICategoryCampaignV2>(campaignId, "category", { category: category }));
  }

  public async getCategoryArticlesCampaignInfo(
    campaignId: string,
    category: string
  ): Promise<IArticleCampaign[] | IArticleCampaignV2[]> {
    return await (this.options.getApiVersion() === 1
      ? this.call<IArticleCampaign[]>(campaignId, "articles", { category: category })
      : this.call<IArticleCampaignV2[]>(campaignId, "articles", { category: category }));
  }

  public async getArticleCampaignInfo(
    campaignId: string,
    category: string,
    papId: string
  ): Promise<IArticleCampaign | IArticleCampaignV2> {
    return await (this.options.getApiVersion() === 1
      ? this.call<IArticleCampaign>(campaignId, "article", { category: category, papId: papId })
      : this.call<IArticleCampaignV2>(campaignId, "article", { category: category, papId: papId }));
  }

  public async getArticleWithExtrasCampaignInfo(
    campaignId: string,
    category: string,
    papId: string,
    selectedExtras: ISelectedExtras
  ): Promise<IArticleCampaign | IArticleCampaignV2> {
    return await (this.options.getApiVersion() === 1
      ? this.call<IArticleCampaign>(campaignId, "articleWithExtras", {
          category: category,
          papId: papId,
          body: selectedExtras
        })
      : this.call<IArticleCampaignV2>(campaignId, "articleWithExtras", {
          category: category,
          papId: papId,
          body: selectedExtras
        }));
  }

  public async getArticlesWithExtrasCampaignInfo(
    campaignId: string,
    category: string,
    articlesWithExtras: ISelectedArticles
  ): Promise<IArticleCampaign[] | IArticleCampaignV2[]> {
    return await (this.options.getApiVersion() === 1
      ? this.call<IArticleCampaign[]>(campaignId, "articlesWithExtras", {
          category: category,
          body: articlesWithExtras
        })
      : this.call<IArticleCampaignV2[]>(campaignId, "articlesWithExtras", {
          category: category,
          body: articlesWithExtras
        }));
  }

  private async call<Type>(
    campaignId: string,
    apiEndpointName: string,
    parameters?: Record<string, string | number | ISelectedExtras | ISelectedArticles>
  ): Promise<Type> {
    const apiEndpoint = apiEndpoints[apiEndpointName];

    // make api call
    const url = getApiEndpointUrl(this.baseUrl, this.options.getApiVersion(), apiEndpoint, {
      campaignId,
      ...parameters
    });

    let bodyOptions;
    if (parameters && parameters.body) {
      bodyOptions = {
        data: JSON.stringify(parameters.body),
        headers: {
          "content-type": "application/json"
        }
      };
    }

    const options = {
      method: apiEndpoint.method,
      ...bodyOptions
    };

    try {
      const response = await axios(url, options);
      return response.data as Type;
    } catch (error: any) {
      if (error.response.status !== 404) {
        throw new Error(`${apiEndpoint.errorMessage}) status ${error.response.status}`);
      }
    }

    return [] as Type;
  }
}
